import { useState, useEffect } from "react"
import { Search, Plus, Filter } from "lucide-react"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Select } from "../ui/select"
import { CustomDropdown } from "../ui/custom-dropdown"
import { attorneysApi, FilterableAttorney } from "../../services/attorneysApi"

interface ControlsBarProps {
  tab?: string
  onTabChange?: (tab: string) => void
  openLeadsCount: number
  closedLeadsCount: number
  recordsPerPage: number
  onRecordsPerPageChange: (value: string) => void
  currentPage: number
  totalPages: number
  onPreviousPage: () => void
  onNextPage: () => void
  onAddLead: () => void
  recordsList: { value: string; label: string }[]
  searchQuery?: string
  onSearchChange?: (query: string) => void
  onSearchInputChange?: (query: string) => void
  currentRecordsCount?: number
  statusFilter?: string
  onStatusChange?: (status: string) => void
}

export function ControlsBar({
  tab,
  onTabChange,
  openLeadsCount,
  closedLeadsCount,
  recordsPerPage,
  onRecordsPerPageChange,
  currentPage,
  totalPages,
  onPreviousPage,
  onNextPage,
  onAddLead,
  recordsList,
  searchQuery = "",
  onSearchChange,
  onSearchInputChange,
  currentRecordsCount = 0,
  statusFilter = "new",
  onStatusChange,
}: ControlsBarProps) {
  const [searchValue, setSearchValue] = useState(searchQuery)
  const [filterableAttorneys, setFilterableAttorneys] = useState<FilterableAttorney[]>([])

  // Load filterable attorneys on mount
  useEffect(() => {
    const loadFilterableAttorneys = async () => {
      try {
        const response = await attorneysApi.getFilterableAttorneys()
        if (!response.error && response.attorneys) {
          setFilterableAttorneys(response.attorneys)
        }
      } catch (error) {
        console.error('Failed to load filterable attorneys:', error)
      }
    }
    loadFilterableAttorneys()
  }, [])

  // Update local search value when prop changes (e.g., when search is cleared from parent)
  useEffect(() => {
    setSearchValue(searchQuery)
  }, [searchQuery])

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
    onSearchInputChange?.(value)
  }

  const handleSearch = () => {
    onSearchChange?.(searchValue)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const clearSearch = () => {
    setSearchValue("")
    onSearchInputChange?.("")
    onSearchChange?.("")
  }

  // Build tab options dynamically with filterable attorneys
  const tabOptions = [
    { value: "new", label: "New" },
    { value: "assigned", label: "Assigned" },
    // Add filterable attorney options dynamically
    ...filterableAttorneys.map(attorney => ({
      value: attorney.filter_value,
      label: `Assigned to ${attorney.first_name}`
    })),
    { value: "referred", label: "Referred" },
    { value: "archived", label: "Archived" },
  ]


  const handleStatusChange = (value: string) => {
    onStatusChange?.(value)
  }

  return (
    <div className="mb-6 space-y-4">
      {/* Desktop Layout */}
      <div className="hidden md:flex items-center justify-between gap-4">
        {/* Left Section - Search and Filters */}
        <div className="flex items-center gap-4 flex-1 max-w-2xl">
          {/* Search Bar */}
          <div className="relative flex-1 max-w-md">
            <Search 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 cursor-pointer hover:text-slate-600" 
              onClick={handleSearch}
            />
            <Input
              placeholder="Search leads by name or phone"
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyPress={handleKeyPress}
              className="pl-10 pr-4"
            />
            {searchValue && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                ×
              </button>
            )}
          </div>

          {/* Status Filter Dropdown */}
          <CustomDropdown
            value={statusFilter}
            onValueChange={handleStatusChange}
            options={tabOptions}
            placeholder="Select status..."
            icon={<Filter className="h-4 w-4 text-slate-500" />}
            className="w-48"            
          />
        </div>

        {/* Right Section - Controls */}
        <div className="flex items-center gap-3">
          {/* Records Per Page */}
          <Select value={recordsPerPage.toString()} onValueChange={onRecordsPerPageChange} className="w-44">
            {recordsList.map((record) => (
              <Select.Option key={record.value} value={record.value}>
                {record.label}
              </Select.Option>
            ))}
          </Select>

          {/* Add Lead Button */}
          <Button onClick={onAddLead} className="bg-slate-900 hover:bg-slate-800 text-white">
            <Plus className="h-4 w-4 mr-2" />
            Add Lead
          </Button>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden space-y-3">
        {/* Add Lead and Records Row */}
        <div className="flex items-center justify-between gap-3">
          {/* Records Per Page - Mobile Dropdown */}
          <Select value={recordsPerPage.toString()} onValueChange={onRecordsPerPageChange} className="w-44">
            {recordsList.map((record) => (
              <Select.Option key={record.value} value={record.value}>
                {record.label}
              </Select.Option>
            ))}
          </Select>

          {/* Add Lead Button */}
          <Button onClick={onAddLead} size="sm" className="bg-slate-900 hover:bg-slate-800 text-white">
            <Plus className="h-4 w-4 mr-1" />
            Add Lead
          </Button>
        </div>

        {/* Search and Filter Row */}
        <div className="flex items-center gap-3">
          {/* Search Bar */}
          <div className="relative flex-1">
            <Search 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 cursor-pointer hover:text-slate-600" 
              onClick={handleSearch}
            />
            <Input
              placeholder="Search leads by name or phone"
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyPress={handleKeyPress}
              className="pl-10 pr-4"
            />
            {searchValue && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                ×
              </button>
            )}
          </div>

          {/* Filter Dropdown */}
          <CustomDropdown
            value={statusFilter}
            onValueChange={handleStatusChange}
            options={tabOptions}
            placeholder="Select status..."
            icon={<Filter className="h-4 w-4 text-slate-500" />}
            className="flex-shrink-0"
            iconOnly={true}
            hideArrow={true}
          />
        </div>
      </div>
    </div>
  )
}
